import { useEffect, useState } from "react";
import { InventoryService } from "../product/Service";
import { CustomerService } from "../customer/Service";
import Select from "react-select";
import { Link, useParams } from "react-router-dom";
import { SaleService } from "./Service";
import { UploadService } from "../../utils/UploadService";
import { toast } from "react-hot-toast";
import { VariantService } from "../variant/Service";
import { SettingService } from "../setting/Service";

const UpdateSale = () => {
  const { id }: any = useParams();
  // Services
  const service = new SaleService();
  const inventoryService = new InventoryService();
  const customerService = new CustomerService();
  const uploadService = new UploadService();
  const variantService = new VariantService();
  const settingService = new SettingService();

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [customer, setCustomer] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [saleMode, setSaleMode] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [amountReceived, setAmountReceived] = useState(0);
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [comments, setComments] = useState("");
  const [discount, setDiscount] = useState(0);
  const [freight, setFreight] = useState(0);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<any[]>([]);
  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [roundOff, setRoundOff] = useState(false);
  const [enableTax, setEnableTax] = useState(false);
  const [tax, setTax] = useState<{ label: string; value: string } | null>(null);
  const [recurringInvoice, setRecurringInvoice] = useState(false);
  const [variantOptions, setVariantOptions] = useState<
    {
      label: string;
      value: string;
      product: any;
      variant: any;
    }[]
  >([]);

  // Options
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [saleModeOptions, setSaleModeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [productOptions, setProductOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [taxTypeOptions, setTaxTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const loadDetails = async () => {
    const sale = await service.getSale(parseInt(id!));
    if (sale) {
      setInvoiceNumber(sale.invoiceNumber);
      setCustomer({ label: sale.customer.name, value: sale.customer.id });
      setInvoiceDate(sale.invoiceDate.split("T")[0].toString());
      setDueDate(
        sale.dueDate != null ? sale.dueDate.split("T")[0].toString() : null
      );
      setSaleMode({ label: sale.saleMode, value: sale.saleMode });
      setAmountReceived(sale.amountReceived);
      setModeOfPayment(sale.modeOfPayment);
      setComments(sale.comments);
      setDiscount(sale.discount);
      setFreight(parseFloat(sale.freight) || 0);
      setProducts(
        sale.products.map((p: any) => ({
          ...p.product,
          quantity: p.quantity,
          salePrice: p.salePrice,
          purchasePrice: p.purchasePrice,
          discount: p.discount,
          variant: p.variant.id,
        }))
      );
      if (sale.tax) {
        setTax({
          label: `${sale.tax.name} (${sale.tax.percentage}%)`,
          value: sale.tax.id,
          //@ts-ignore
          percentage: sale.tax.percentage,
        });
      }
      setEnableTax(sale.enableTax);
      setRecurringInvoice(sale.recurringInvoice);
    }
  };

  const loadVariants = async () => {
    const variants = await variantService.all();
    setVariantOptions(
      variants.map((v: any) => ({
        label: `${v.size} (${v.unit}) / ${v.type}`,
        value: v.id,
        product: v.product,
        variant: v,
      }))
    );
  };

  useEffect(() => {
    loadDetails();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Product should not be empty
    if (products.length === 0) {
      toast.error("Please add products");
      return;
    }
    toast.loading("Updating sale...");
    const data = {
      invoiceNumber: invoiceNumber,
      customer: customer?.value,
      invoiceDate,
      dueDate,
      saleMode: saleMode?.value,
      tax: tax?.value,
      amountReceived,
      modeOfPayment,
      comments,
      discount: parseFloat(discount.toString()),
      freight,
      enableTax,
      recurringInvoice,
      products: products.map((p: any) => ({
        product: p.id,
        quantity: p.quantity,
        purchasePrice: p.purchasePrice,
        salePrice: p.salePrice,
        discount: parseFloat(p.discount.toString()),
        variant: p.variant,
      })),
      roundOff,
    };
    const res = await service.updateSale(parseInt(id!), data);
    toast.dismiss();
    if (res) {
      toast.success("Sale updated successfully");
    }
  };

  // Set options
  useEffect(() => {
    const fetchCustomers = async () => {
      const customers = await customerService.all();
      setCustomerOptions(
        customers
          .filter((c: any) => c.type === "Customer")
          .map((v: any) => ({
            label: v.name,
            value: v.id,
          }))
      );
    };
    const fetchProducts = async () => {
      const products = await inventoryService.allProducts();
      setProductOptions(
        products.map((v: any) => ({ label: v.name, value: v.id }))
      );
      setAllProducts(products.map((v: any) => ({ ...v, quantity: 1 })));
    };
    const purchaseModeOptions = [
      { label: "Cash", value: "Cash" },
      { label: "Credit", value: "Credit" },
    ];
    setSaleModeOptions(purchaseModeOptions);
    fetchCustomers();
    fetchProducts();
    loadVariants();
  }, []);

  useEffect(() => {
    const getTaxTypes = async () => {
      const res = await settingService.allTaxTypes();
      if (res) {
        const options = res.map((item: any) => {
          return {
            label: `${item.name} (${item.percentage}%)`,
            value: item.id,
            percentage: item.percentage,
          };
        });
        setTaxTypeOptions(options);
      }
    };
    getTaxTypes();
  }, []);

  useEffect(() => {
    const taxableAmount = products.reduce((acc: number, product: any) => {
      return (
        acc +
        product.quantity * product.salePrice -
        (product.quantity * product.salePrice * product.discount) / 100
      );
    }, 0);
    setTaxableAmount(taxableAmount);

    // Calculate discount
    const totalDiscount = discount > 0 ? (taxableAmount * discount) / 100 : 0;
    setTotalDiscount(totalDiscount);

    // Calculate tax
    let taxAmount = 0;
    if (tax) {
      //@ts-ignore
      taxAmount = ((taxableAmount - totalDiscount) * tax.percentage) / 100;
    }

    const total = taxableAmount + taxAmount - totalDiscount + (freight || 0);
    setTotal(total);
  }, [products, roundOff, discount, tax, freight]);

  return (
    <>
      <div className="card mb-0">
        <div className="card-body">
          <div className="content-page-header">
            <h5>Update Sale</h5>
            <div className="list-btn">
              <ul className="filter-list">
                <li>
                  <Link
                    className="btn btn-primary"
                    target={"_blank"}
                    to={`/sales/details/${id}/invoice`}
                  >
                    <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                    Invoice
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="row">
            <div className="col-md-12">
              <div className="form-group-item border-0 mb-0">
                <div className="row align-item-center">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Invoice Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Purchases Number"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Select Customer <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="customer"
                            options={customerOptions}
                            value={customer}
                            onChange={setCustomer}
                            required={true}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/customers/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Invoice Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="datetimepicker form-control"
                        placeholder="Select Date"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>Due Date</label>
                      <input
                        type="date"
                        className="datetimepicker form-control"
                        placeholder="Select Due Date"
                        value={dueDate || ""}
                        onChange={(e) => setDueDate(e.target.value)}
                        required={false}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>Sale Mode</label>
                      <Select
                        name="sale-mode"
                        options={saleModeOptions}
                        value={saleMode}
                        onChange={setSaleMode}
                        required={false}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-12 mb-3">
                    <label htmlFor="tax" className="form-label">
                      Tax
                    </label>
                    <Select
                      name="tax"
                      options={taxTypeOptions}
                      value={tax}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      onChange={setTax}
                    />
                  </div>
                  {saleMode?.value === "Credit" && (
                    <>
                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-block mb-3">
                          <label>Amount Received</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter total amount received"
                            value={amountReceived}
                            onChange={(e) =>
                              setAmountReceived(parseInt(e.target.value))
                            }
                          />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-block mb-3">
                          <label>Mode of Payment</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Mode of Payment"
                            value={modeOfPayment}
                            onChange={(e) => setModeOfPayment(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>Comment</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Comment"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>Discount(%)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Discount Percentage"
                        value={discount}
                        onChange={(e) =>
                          setDiscount(parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>Freight Charges</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Freight Charges"
                        value={freight}
                        onChange={(e) =>
                          setFreight(parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="input-block mb-3">
                      <label>
                        Products <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="product"
                            options={productOptions}
                            onChange={(e) => {
                              const product = allProducts.find(
                                (p: any) => p.id === e?.value
                              );
                              if (product) {
                                setProducts([...products, product]);
                              }
                            }}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/products/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group-item">
                <div className="card-table">
                  <div className="card-body">
                    <div className="table-responsive no-pagination">
                      <table className="table table-center table-hover datatable">
                        <thead className="thead-light">
                          <tr>
                            <th>Product / Service</th>
                            <th>Variant</th>
                            <th style={{ width: "100px" }}>Quantity</th>
                            <th style={{ width: "200px" }}>Rate</th>
                            <th>Discount</th>
                            <th>Amount</th>
                            <th className="no-sort">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product: any, index) => (
                            <tr key={product.id}>
                              <td>{product.name}</td>
                              <td>
                                <div className=" mb-3">
                                  <Select
                                    name="variant"
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    value={variantOptions.find(
                                      (v: any) =>
                                        v.product["id"] === product.id &&
                                        v.variant["id"] === product.variant
                                    )}
                                    options={variantOptions.filter(
                                      (v: any) => v.product["id"] === product.id
                                    )}
                                    onChange={(e) => {
                                      const newProducts = products.map(
                                        (p: any, i: number) => {
                                          if (i === index) {
                                            return {
                                              ...p,
                                              variant: parseInt(
                                                e?.value ?? "0"
                                              ),
                                              purchasePrice:
                                                e?.variant?.salePrice ?? 0,
                                              salePrice:
                                                e?.variant?.salePrice ?? 0,
                                              discount:
                                                e?.variant?.discount ?? 0,
                                            };
                                          }
                                          return p;
                                        }
                                      );
                                      setProducts(newProducts);
                                    }}
                                  />
                                </div>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  style={{
                                    minWidth: "60px",
                                    maxWidth: "100px",
                                    padding: "2px 6px",
                                  }}
                                  placeholder="Qty"
                                  value={product.quantity}
                                  onChange={(e) => {
                                    const newProducts = products.map(
                                      (p: any, i: number) => {
                                        if (i === index) {
                                          return {
                                            ...p,
                                            quantity: parseInt(e.target.value),
                                          };
                                        }
                                        return p;
                                      }
                                    );
                                    setProducts(newProducts);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder="Enter purchase price"
                                  value={product.salePrice}
                                  onChange={(e) => {
                                    const newProducts = products.map(
                                      (p: any, i: number) => {
                                        if (i === index) {
                                          return {
                                            ...p,
                                            salePrice: parseInt(e.target.value),
                                          };
                                        }
                                        return p;
                                      }
                                    );
                                    setProducts(newProducts);
                                  }}
                                />
                              </td>
                              <td>{product.discount} %</td>
                              <td>
                                ₹
                                {(
                                  product.salePrice * product.quantity -
                                  (product.salePrice *
                                    product.quantity *
                                    product.discount) /
                                    100
                                ).toFixed(2)}
                              </td>
                              <td className="d-flex align-items-center h-100">
                                <button
                                  className="btn btn-danger btn-sm mb-2"
                                  onClick={() => {
                                    const newProducts = products.filter(
                                      (p: any, i: number) => i !== index
                                    );
                                    setProducts(newProducts);
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group-item border-0 p-0">
                <div className="row">
                  <div className="col-12">
                    <div className="form-group-bank">
                      <div className="invoice-total-box">
                        <div className="invoice-total-inner">
                          <p>
                            Taxable Amount{" "}
                            <span>₹{taxableAmount.toFixed(2)}</span>
                          </p>
                          <p>
                            Discount <span>₹{totalDiscount.toFixed(2)}</span>
                          </p>
                          <p>
                            Tax{" "}
                            <span>
                              ₹
                              {
                                // @ts-ignore
                                tax
                                  ? (
                                      ((taxableAmount - totalDiscount) *
                                        //@ts-ignore
                                        tax.percentage) /
                                      100
                                    ).toFixed(2)
                                  : (0).toFixed(2)
                              }
                            </span>
                          </p>
                          <p>
                            Freight <span>₹{(freight || 0).toFixed(2)}</span>
                          </p>
                          {/*<div className="status-toggle justify-content-between">*/}
                          {/*    <div className="d-flex align-center">*/}
                          {/*        <p>Round Off </p>*/}
                          {/*        <input*/}
                          {/*            id="rating_1"*/}
                          {/*            className="check"*/}
                          {/*            type="checkbox"*/}
                          {/*            defaultChecked={false}*/}
                          {/*            onChange={(e) => {*/}
                          {/*                setRoundOff(e.target.checked);*/}
                          {/*            }}*/}
                          {/*        />*/}
                          {/*        <label*/}
                          {/*            htmlFor="rating_1"*/}
                          {/*            className="checktoggle checkbox-bg"*/}
                          {/*        >*/}
                          {/*            checkbox*/}
                          {/*        </label>*/}
                          {/*    </div>*/}
                          {/*    <span>₹{total}</span>*/}
                          {/*</div>*/}
                        </div>
                        <div className="invoice-total-footer">
                          <h4>
                            Total Amount <span>₹{total.toFixed(2)}</span>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="btn-path text-end">
                <button
                  type="reset"
                  onClick={() => {
                    window.history.back();
                  }}
                  className="btn btn-cancel bg-primary-light me-2"
                >
                  Back
                </button>
                <button type="submit" className="btn btn-primary">
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateSale;
